import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';

import { Nullable } from '@clearverify/types';

import { useSendInvitesToVerifiersStaffMutation, VerifierStaffListDocument } from 'src/api';
import { Button, FieldArrayTemplate, Tooltip } from 'src/components/common';
import { CheckboxField, Field, Form, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { validateWithYupSchema } from 'src/utils/yup';

import { tooltipText } from './consts';
import {
  InviteFormFields,
  InviteFormFieldsForStaff,
  InviteFormFieldsValues,
  InviteFormFieldsValuesForStaff,
} from './types';
import { inviteFormValidationSchema } from './utils/validation';

interface InviteVerifierStaffModalFormProps {
  verifierId?: Nullable<string>;
  isDisabled?: boolean;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InviteVerifierStaffModalForm: React.FC<InviteVerifierStaffModalFormProps> = ({
  verifierId,
  isDisabled,
  closeModal,
  switchClosability,
}) => {
  const emptyStaffObject: InviteFormFieldsValuesForStaff = {
    email: null,
    isAdmin: !!isDisabled,
  };

  const initialValues: InviteFormFieldsValues = {
    [InviteFormFields.staff]: [emptyStaffObject],
  };

  const { verifierId: verifierIdCurrentUser } = useCurrentUserVerifier();
  const { createApolloContext } = useNotification();

  const [sendInvites] = useSendInvitesToVerifiersStaffMutation({
    refetchQueries: [VerifierStaffListDocument],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const handleSubmit = React.useCallback(
    async (formFields: InviteFormFieldsValues) => {
      switchClosability(false);

      try {
        await sendInvites({
          variables: {
            verifierId: verifierId || verifierIdCurrentUser || '',
            verifierStaff: formFields.staff.map(staff => ({
              ...staff,
              email: String(staff.email),
            })),
          },
        });

        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [switchClosability, sendInvites, verifierId, verifierIdCurrentUser, closeModal],
  );

  return (
    <React.Fragment>
      <Typography color="GrayText" variant="body1" component="h4" mb={3} lineHeight="24px">
        Invite Staff by using their email address
      </Typography>
      <Form<InviteFormFieldsValues>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        subscription={{ valid: true, values: true, submitting: true, errors: true }}
        initialValuesEqual={() => true}
        containerProps={{ display: 'flex', flexDirection: 'column' }}
        validate={validateWithYupSchema(inviteFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <FieldArrayTemplate
                label="Invitation"
                name={InviteFormFields.staff}
                addIcon={<AddIcon />}
                onAdd={fields => fields.push(emptyStaffObject)}
                onRemove={(fields, index) => fields.remove(index)}
              >
                {({ renderFieldName }) => (
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 12,
                      }}
                    >
                      <Field
                        name={renderFieldName(InviteFormFieldsForStaff.email)}
                        component={TextField}
                        label="Email Address"
                        fullWidth
                        disabled={submitting}
                        required
                      />
                    </Grid>

                    <Grid
                      size={{
                        xs: 12,
                        md: 12,
                      }}
                    >
                      {isDisabled ? (
                        <Tooltip title={tooltipText} arrow placement="bottom">
                          <Box pl={2} mt={-1} display="inline-block">
                            <Field
                              name={renderFieldName(InviteFormFieldsForStaff.isAdmin)}
                              component={CheckboxField}
                              label="This user is Admin"
                              fullWidth
                              disabled
                              required
                            />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box pl={2} mt={-1} display="inline-block">
                          <Field
                            name={renderFieldName(InviteFormFieldsForStaff.isAdmin)}
                            component={CheckboxField}
                            label="This user is Admin"
                            fullWidth
                            disabled={submitting}
                            required
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                )}
              </FieldArrayTemplate>
              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  type="reset"
                  color="error"
                  variant="outlined"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </React.Fragment>
  );
};
